<script setup lang="ts">
import { computed, ref, toRef } from "vue";
import { useDisplay } from "vuetify";
import PatientChatDockTab from "@/components/models/message/chatDock/tabs/PatientChatDockTab.vue";
import InvitesChatDockTab from "@/components/models/message/chatDock/tabs/InvitesChatDockTab.vue";
import ChatDockSkeleton from "@/components/models/message/skeletons/ChatDockSkeleton.vue";
import { useChatDock } from "@/composables/models/message/useChatDock.ts";
import { useChatDockChrome } from "@/composables/models/message/useChatDockChrome.ts";
import { useOpenMobileChatDock } from "@/composables/models/message/openMobileChatDock.ts";

const props = withDefaults(
  defineProps<{
    sticky?: boolean;
    onlyPatients?: boolean;
    onlyInvites?: boolean;
    hideCollapsable?: boolean;
    extend?: boolean;
  }>(),
  { extend: false }
);

const { mdAndUp, lgAndDown } = useDisplay();
const openMobileChatDock = useOpenMobileChatDock();

const isRail = computed(() => lgAndDown.value);
const {
  shellClass,
  controlSurfaceClass,
  controlSurfaceSubtleClass,
  mutedIconClass,
  dividerColor,
  inputColor,
  tabBtnColor,
  tabBtnClass,
} = useChatDockChrome();

const patientsLoading = ref(true);
const invitesLoading = ref(true);

const {
  search,
  collapsed,
  activeTab,
  tabs,
  title,
  searchPlaceholder,
  selectedId,
  toggleCollapsed,
  onSelect,
} = useChatDock({
  onlyPatients: toRef(props, "onlyPatients"),
  onlyInvites: toRef(props, "onlyInvites"),
  extend: toRef(props, "extend"),
});

const isCollapsed = computed(() => isRail.value || collapsed.value);

const onToggleClick = () => {
  if (isRail.value) {
    openMobileChatDock?.();
    return;
  }

  toggleCollapsed();
};
</script>

<template>
  <template v-if="mdAndUp">
    <div
      class="tw:!pl-2 tw:!pr-4 tw:!py-2 tw:transition-[width,min-width] tw:duration-200 tw:ease-linear"
      :class="[
        shellClass,
        {
          'tw:fixed tw:left-0 tw:bottom-0 tw:top-[78px]': !sticky,
          'tw:h-full': sticky,
          'tw:w-[72px] tw:min-w-[72px]': isCollapsed,
          'tw:w-64 tw:min-w-64 tw:max-w-64': !isCollapsed,
        },
      ]"
    >
      <div class="tw:h-full tw:flex tw:flex-col">
        <div
          :class="[
            'tw:flex tw:items-center',
            isCollapsed ? 'tw:justify-center' : 'tw:justify-between tw:!px-2',
          ]"
        >
          <div
            v-if="!isCollapsed"
            class="tw:flex tw:items-center tw:gap-2"
          >
            <v-icon
              icon="mdi-forum-outline"
              size="18"
            />
            <div class="tw:text-sm tw:font-extrabold">{{ title }}</div>
          </div>

          <v-btn
            v-if="!hideCollapsable"
            size="small"
            variant="flat"
            class="tw:!rounded-xl tw:!min-w-0 tw:!h-10 tw:!w-10 tw:!p-0"
            :class="controlSurfaceSubtleClass"
            @click="onToggleClick"
          >
            <v-icon
              :class="mutedIconClass"
              :icon="isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left'"
            />
          </v-btn>
        </div>

        <v-divider
          class="tw:!my-3"
          :color="dividerColor"
        />

        <div
          v-if="!isCollapsed"
          class="tw:px-3 tw:pb-2"
        >
          <v-text-field
            v-model="search"
            :color="inputColor"
            density="compact"
            variant="plain"
            hide-details
            :placeholder="searchPlaceholder"
            flat
            class="tw:!text-xs tw:!rounded-md tw:!px-2 tw:!pb-1.5"
            :class="controlSurfaceClass"
            prepend-inner-icon="mdi-magnify"
          />

          <v-btn-group
            v-if="!onlyPatients"
            class="tw:!h-8 tw:w-full tw:shrink-0 tw:!mt-2 tw:rounded-none"
          >
            <v-btn
              v-for="tab in tabs"
              :key="tab.key"
              :disabled="tab.disabled"
              :color="tabBtnColor(activeTab === tab.key)"
              class="tw:!flex-1 tw:!min-w-0 tw:!rounded-md"
              :class="tabBtnClass(activeTab === tab.key)"
              density="compact"
              variant="flat"
              @click="activeTab = tab.key"
            >
              <span
                class="tw:text-xs tw:flex tw:items-center tw:justify-center tw:gap-1 tw:min-w-0 tw:truncate"
              >
                <v-icon
                  :icon="tab.icon"
                  class="tw:shrink-0"
                />
                <span class="tw:truncate">{{ tab.name }}</span>
              </span>
            </v-btn>
          </v-btn-group>
        </div>

        <v-divider
          v-if="!isCollapsed"
          class="tw:!my-3"
          :color="dividerColor"
        />

        <div class="tw:grow tw:min-h-0 tw:overflow-y-auto tw:!pt-1 no-scroll-bar">
          <template v-if="activeTab === 'patients' ? patientsLoading : invitesLoading">
            <chat-dock-skeleton :collapsed="isCollapsed" />
          </template>

          <patient-chat-dock-tab
            v-show="activeTab === 'patients' && !patientsLoading"
            :active="activeTab === 'patients'"
            :search="search"
            :collapsed="isCollapsed"
            :selected-id="selectedId"
            @select="onSelect"
            @update:loading="patientsLoading = $event"
          />

          <invites-chat-dock-tab
            v-show="activeTab === 'invites' && !invitesLoading"
            :active="activeTab === 'invites'"
            :search="search"
            :collapsed="isCollapsed"
            :selected-id="selectedId"
            @select="onSelect"
            @update:loading="invitesLoading = $event"
          />
        </div>
      </div>
    </div>
  </template>
</template>

<style scoped>
.no-scroll-bar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.no-scroll-bar::-webkit-scrollbar {
  display: none;
}
</style>
